import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Brush, Sparkles, Scan, Clock, Users, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Navigation from "@/components/Navigation";
import bg1Hero from "@/assets/robots/pudu-bg1-hero.png";
import bg1Perception from "@/assets/robots/pudu-bg1-perception.jpeg";

const PuduBG1 = () => {
  const benefits = [
    { Icon: Brush, title: "One-Pass Sweep & Scrub", desc: "Combines sweeping and deep scrubbing in one efficient workflow with smart dosing and ultra-wide path clearance." },
    { Icon: Settings, title: "Extendable Edge Cleaning", desc: "Scrubbing brush physically extends to reach walls and shelving edges, minimizing manual touch-ups." },
    { Icon: Sparkles, title: "AI Magic Cleaning", desc: "AI-driven adaptive mechanism and milliliter-accurate auto-dosing match conditions and reduce waste." },
    { Icon: Scan, title: "3D Perception Enhancement", desc: "3D VSLAM + LiDAR multi-sensor fusion delivers reliable navigation even in dark or high-interference areas." },
    { Icon: Clock, title: "24/7 Continuous Operation", desc: "Optional all-in-one workstation automates refilling, drainage, and charging for mission-ready uptime." },
    { Icon: Users, title: "Human-Robot Interaction", desc: "Ride-on mode for instant manual control, audio-visual safety alerts, and PUDU Link connectivity." },
  ];

  const specs = [
    { label: "Scrubbing Width", value: "550 mm" },
    { label: "Cleaning Width (with side brushes)", value: "708 mm" },
    { label: "Runtime", value: "Up to 7.5 hours" },
    { label: "Clean Water Tank", value: "75 L" },
    { label: "Waste Water Tank", value: "60 L" },
    { label: "Dual Agent Tanks", value: "~7 L total" },
    { label: "Trashbin Capacity", value: "5 L" },
    { label: "Weight", value: "~344 kg (758 lbs)" },
    { label: "Dimensions (with squeegee)", value: "1195 × 760 × 1303 mm" },
    { label: "Covered Cleaning Mode", value: "Up to 2,000 m²/h" },
    { label: "Spot Cleaning Mode", value: "Up to 6,000 m²/h" },
    { label: "Battery", value: "90 Ah (48V) — ~180 Ah equivalent at 24V" },
    { label: "Communications", value: "4G, Wi-Fi, Bluetooth" },
    { label: "IoT Integration", value: "Optional E-gate & elevator control" },
    { label: "Workstation", value: "Optional charging dock for unattended operation" },
  ];

  const useCases = [
    "Airports", "Warehouses", "Hospitals", "Schools & Universities",
    "Shopping Centers", "Large Retail", "Manufacturing", "Convention Centers",
    "Transportation Hubs", "Commercial Cleaning Teams",
  ];

  return (
    <>
      <SEO
        title="PUDU BG1 Series — AI-Native Large Scrubber-Dryer Robots | Neil AI"
        description="PUDU BG1 and BG1 Pro: AI-native large scrubber-dryer robots with one-pass sweep & scrub, extendable edge cleaning, 3D perception, and 24/7 continuous operation."
        keywords="PUDU BG1, PUDU BG1 Pro, large scrubber dryer robot, commercial cleaning robot, airport cleaning robot, warehouse cleaning robot, autonomous scrubber"
      />
      <Navigation />
      <div className="min-h-screen pt-32 md:pt-40 pb-12 px-4" style={{
        background: `radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)`
      }}>
        <main className="max-w-7xl mx-auto">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back to Robots
            </Link>
          </Button>

          {/* Hero */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16 items-center">
            <div>
              <Badge className="mb-3 bg-primary/15 text-primary border border-primary/30">Commercial Cleaning Robots</Badge>
              <p className="text-primary font-semibold mb-2">AI-Native Large Scrubber-Dryer</p>
              <h1 className="font-orbitron font-bold text-3xl md:text-5xl lg:text-6xl mb-6">
                PUDU <span className="text-primary">BG1 Series</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed">
                Built for large commercial and industrial spaces that need powerful autonomous floor cleaning, consistent coverage, and lower manual workload. Sweeping, scrubbing, AI-powered detection, and smart maintenance in one intelligent cleaning system.
              </p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <Card className="glass-effect border-[hsl(var(--line))] p-4 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-primary mb-1">6,000</p>
                  <p className="text-xs text-muted-foreground">m²/h Spot Mode</p>
                </Card>
                <Card className="glass-effect border-[hsl(var(--line))] p-4 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-primary mb-1">7.5h</p>
                  <p className="text-xs text-muted-foreground">Runtime</p>
                </Card>
                <Card className="glass-effect border-[hsl(var(--line))] p-4 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-primary mb-1">75L</p>
                  <p className="text-xs text-muted-foreground">Clean Water</p>
                </Card>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" variant="secondary" className="glow-gold">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Request BG1 Quote
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book Facility Assessment
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/robots">Compare Cleaning Robots</Link>
                </Button>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[hsl(var(--line))] bg-white/5">
              <img src={bg1Hero} alt="PUDU BG1 Series large scrubber-dryer robot" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Benefits */}
          <section className="mb-16">
            <h2 className="font-orbitron font-bold text-3xl mb-2 text-center">Built for <span className="text-primary">Large-Scale Cleaning</span></h2>
            <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">Engineered for facilities that demand consistency, coverage, and round-the-clock readiness.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {benefits.map(({ Icon, title, desc }) => (
                <Card key={title} className="glass-effect border-[hsl(var(--line))] p-6 hover:border-primary/40 transition-colors">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* BG1 vs BG1 Pro */}
          <section className="mb-16">
            <h2 className="font-orbitron font-bold text-3xl mb-8 text-center">BG1 vs <span className="text-primary">BG1 Pro</span></h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="glass-effect border-[hsl(var(--line))] p-8">
                <h3 className="font-orbitron font-bold text-2xl mb-2">PUDU BG1</h3>
                <p className="text-sm text-muted-foreground mb-4">AI-native large scrubber-dryer for consistent autonomous cleaning across large commercial floors.</p>
                <ul className="space-y-2 text-sm">
                  {["One-pass sweep & scrub", "Extendable edge cleaning", "AI Magic Cleaning & auto-dosing", "LiDAR + VSLAM navigation", "Up to 7.5 h runtime"].map((f) => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> {f}</li>
                  ))}
                </ul>
              </Card>
              <Card className="glass-effect border-primary/40 p-8 relative">
                <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground">Enhanced</Badge>
                <h3 className="font-orbitron font-bold text-2xl mb-2">PUDU BG1 Pro</h3>
                <p className="text-sm text-muted-foreground mb-4">Enhanced model with 3D LiDAR + 3D VSLAM fusion positioning and stronger perception for complex large-area environments.</p>
                <ul className="space-y-2 text-sm">
                  {["Everything in BG1", "3D LiDAR + 3D VSLAM fusion positioning", "Stronger perception in dark / high-interference areas", "Ideal for airports, warehouses, convention centers", "Optional E-gate & elevator integration"].map((f) => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> {f}</li>
                  ))}
                </ul>
              </Card>
            </div>
          </section>

          {/* 3D Perception feature */}
          <section className="mb-16">
            <Card className="glass-effect border-[hsl(var(--line))] overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-0 items-stretch">
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <Badge className="mb-3 bg-primary/15 text-primary border border-primary/30 w-fit">AI Vision</Badge>
                  <h2 className="font-orbitron font-bold text-3xl md:text-4xl mb-4">3D Perception <span className="text-primary">Enhancement</span></h2>
                  <p className="text-muted-foreground leading-relaxed">
                    PUDU BG1 Series integrates 3D sensing and high-performance AI computing for precise recognition and dynamic avoidance. It ensures stable mapping and localization across high-clearance, nighttime, and high-interference scenarios, maximizing autonomous cleaning efficiency.
                  </p>
                </div>
                <div className="min-h-[280px] lg:min-h-[420px] bg-black/40">
                  <img src={bg1Perception} alt="PUDU BG1 3D perception and AI computing visualization" className="w-full h-full object-cover" />
                </div>
              </div>
            </Card>
          </section>

          {/* Specs */}
          <section className="mb-16">
            <h2 className="font-orbitron font-bold text-3xl mb-8">Technical Specifications</h2>
            <Card className="glass-effect border-[hsl(var(--line))] p-6">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                {specs.map((s) => (
                  <div key={s.label} className="border-b border-[hsl(var(--line))] pb-3">
                    <p className="text-sm text-muted-foreground mb-1">{s.label}</p>
                    <p className="font-semibold">{s.value}</p>
                  </div>
                ))}
              </div>
            </Card>
          </section>

          {/* Use cases */}
          <section className="mb-16">
            <h2 className="font-orbitron font-bold text-3xl mb-8">Best For</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {useCases.map((u) => (
                <Card key={u} className="glass-effect border-[hsl(var(--line))] p-5 text-center">
                  <p className="font-semibold text-sm">{u}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* Staff support */}
          <section className="mb-16">
            <Card className="glass-effect border-[hsl(var(--line))] p-8 md:p-10">
              <h2 className="font-orbitron font-bold text-2xl md:text-3xl mb-4">Support Cleaning Teams. Reduce Burnout.</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The BG1 Series helps businesses reduce manual cleaning pressure, improve cleaning consistency, support overstretched teams, and cover larger spaces with intelligent autonomous cleaning workflows.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Designed to relieve cleaning teams from repetitive manual scrubbing, touch-up work, edge cleaning, and high-traffic spill response — so staff can focus on higher-value work.
              </p>
            </Card>
          </section>

          {/* CTA */}
          <section className="mb-10">
            <Card className="glass-effect border-[hsl(var(--line))] p-8 md:p-10 text-center">
              <h2 className="font-orbitron font-bold text-2xl md:text-3xl mb-4">Deploy the BG1 Series at your facility</h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">Get a tailored quote, book a facility assessment, or explore financing through our partner Ascentium.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" variant="secondary" className="glow-gold">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Request BG1 Quote
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="tel:3369624539">Talk To a Specialist</a>
                </Button>
              </div>
            </Card>
          </section>

          <p className="text-xs text-muted-foreground text-center">
            Product specifications based on official PUDU BG1 Series product information.
          </p>
        </main>
      </div>
    </>
  );
};

export default PuduBG1;
